import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Star, Clock, Calendar, Target, Award, Camera, MapPin, Navigation, ChevronRight } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { parseLocalDate } from '../../utils/calculations';
import VoteDetailModal from '../../components/modals/VoteDetailModal';
import logger from '../../utils/logger';
import { resizeImageToBase64 } from '../../utils/imageUtils';
import { geocodeCity } from '../../utils/geo';
import { EMOJI_OPTIONS } from '../../constants/data';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import StarRating from '../../components/ui/StarRating';
import CityAutocomplete from '../../components/ui/CityAutocomplete';
import TeamPicker from '../../components/ui/TeamPicker';

/* Collapsible avatar picker — hidden input carries value for FormData */
const AvatarPickerRadio = ({ defaultValue }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue || EMOJI_OPTIONS[0]);
  return (
    <div>
      <input type="hidden" name="photo" value={selected} />
      <div className="flex items-center gap-4 bg-slate-50 border border-slate-100 rounded-xl p-3 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-3xl shadow-inner border border-slate-200 shrink-0">{selected}</div>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-700">Avatar</div>
          <span className="text-xs text-blue-600 font-medium">{open ? '▲ Fechar' : '▼ Trocar avatar'}</span>
        </div>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-8 gap-1.5 bg-slate-50 p-3 rounded-xl border border-slate-100">
          {EMOJI_OPTIONS.map(emoji => (
            <button
              key={emoji}
              type="button"
              onClick={() => { setSelected(emoji); setOpen(false); }}
              className={`h-10 flex items-center justify-center text-xl rounded-lg transition-all ${selected === emoji ? 'bg-blue-500' : 'hover:bg-slate-200'}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const maskPhone = (v) => v.replace(/\D/g,'').replace(/(\d{2})(\d)/,'($1) $2').replace(/(\d{5})(\d)/,'$1-$2').slice(0,15);

/* Phone input with fixed +55 country code.
   Uncontrolled mode: pass defaultValue (FormData via name attr).
   Controlled mode: pass value + onChange (no name attr). */
const PhoneInput55Field = ({ defaultValue, value: controlledValue, onChange: controlledOnChange, name = 'phone' }) => {
  const isControlled = controlledValue !== undefined;
  const [val, setVal] = React.useState(defaultValue || '');
  const displayVal = isControlled ? controlledValue : val;
  const handleChange = (e) => {
    const masked = maskPhone(e.target.value);
    if (isControlled) {
      controlledOnChange(masked);
    } else {
      setVal(masked);
    }
  };
  return (
    <div className="flex">
      <span className="flex items-center gap-1 px-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-sm font-medium text-slate-600 whitespace-nowrap">
        🇧🇷 +55
      </span>
      <input
        name={isControlled ? undefined : name}
        type="tel"
        value={displayVal}
        className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-r-xl outline-none"
        placeholder="(xx) 9xxxx-xxxx"
        maxLength={15}
        onChange={handleChange}
      />
    </div>
  );
};

const WEEK_DAYS = [
  { id: 'seg', label: 'Seg' }, { id: 'ter', label: 'Ter' }, { id: 'qua', label: 'Qua' },
  { id: 'qui', label: 'Qui' }, { id: 'sex', label: 'Sex' }, { id: 'sab', label: 'Sáb' },
  { id: 'dom', label: 'Dom' },
];

const initAvailability = (prof) => {
  const base = { seg: null, ter: null, qua: null, qui: null, sex: null, sab: null, dom: null };
  if (!prof?.availability) return base;
  const a = prof.availability;
  const result = { ...base };
  Object.keys(base).forEach(day => {
    const v = a[day];
    if (!v) { result[day] = null; return; }
    if (Array.isArray(v)) { result[day] = v; return; }
    // migrar formato antigo { start, end } → array
    if (v.start !== undefined || v.end !== undefined) {
      result[day] = [{ start: v.start || '', end: v.end || '' }];
    }
  });
  // migrar formato muito antigo { days: [], timeStart, timeEnd }
  if (Array.isArray(a.days)) {
    a.days.forEach(d => {
      if (base.hasOwnProperty(d)) result[d] = [{ start: a.timeStart || '19:00', end: a.timeEnd || '22:00' }];
    });
  }
  return result;
};

const ProfilePage = () => {
  const { profile, updateProfile, allGames, duels, userId } = useAppContext();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [manualRating, setManualRating] = useState(profile?.manualRating || 5);
  const [activeTab, setActiveTab] = useState('perfil');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(profile?.photoURL || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [city, setCity] = useState(profile?.city || '');
  const [geoLoading, setGeoLoading] = useState(false);
  const [availability, setAvailability] = useState(() => initAvailability(profile));
  const [allowWhatsApp, setAllowWhatsApp] = useState(!!profile?.whatsappPhone);
  const [voteDetail, setVoteDetail] = useState(null);

  const openVoteDetail = (h) => {
    if (h.voteDistribution) {
      const total = Object.values(h.voteDistribution).reduce((a, b) => a + b, 0) + (h.outlierVotes?.length || 0);
      setVoteDetail({ distribution: h.voteDistribution, outlierVotes: h.outlierVotes || [], filteredAvg: h.rating, totalVotes: total });
      return;
    }
    const game = allGames.find(g => g.id === h.gameId && g.groupId === h.groupId);
    if (!game?.votes) { setVoteDetail({ noData: true }); return; }
    const rawVotes = Object.values(game.votes).map(sheet => sheet[userId]).filter(v => v !== undefined && v !== null);
    if (rawVotes.length === 0) { setVoteDetail({ noData: true }); return; }
    const distribution = {};
    rawVotes.forEach(v => { const k = String(v); distribution[k] = (distribution[k] || 0) + 1; });
    setVoteDetail({ distribution, outlierVotes: [], filteredAvg: h.rating, totalVotes: rawVotes.length });
  };

  const playerStats = useMemo(() => {
    if (!allGames || !userId) return { games: 0, goals: 0, assists: 0 };
    let games = 0, goals = 0, assists = 0;
    allGames.forEach(g => {
      if (g.status !== 'finished') return;
      if (!g.confirmedIds?.includes(userId)) return;
      games++;
      const stats = g.playerStats?.[userId];
      if (stats) {
        goals += stats.goals || 0;
        assists += stats.assists || 0;
      }
    });
    (duels || []).forEach(duel => {
      if (duel.status !== 'finished') return;
      const roster = [...(duel.creatorRoster || []), ...(duel.opponentRoster || [])];
      if (!roster.some(p => p.id === userId && !p.isGuest)) return;
      games++;
      const stats = duel.playerStats?.[userId];
      if (stats) { goals += stats.goals || 0; assists += stats.assists || 0; }
    });
    return { games, goals, assists };
  }, [allGames, duels, userId]);

  if (!profile) return null;

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) { showError('Geolocalização não suportada.'); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=pt&zoom=10`,
            { headers: { 'User-Agent': 'PeladaMatch/1.0' } }
          );
          const data = await res.json();
          const addr = data.address || {};
          const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.county || addr.state_district || '';
          const STATE_UF = {
            'Acre':'AC','Alagoas':'AL','Amapá':'AP','Amazonas':'AM','Bahia':'BA',
            'Ceará':'CE','Distrito Federal':'DF','Espírito Santo':'ES','Goiás':'GO',
            'Maranhão':'MA','Mato Grosso':'MT','Mato Grosso do Sul':'MS',
            'Minas Gerais':'MG','Pará':'PA','Paraíba':'PB','Paraná':'PR',
            'Pernambuco':'PE','Piauí':'PI','Rio de Janeiro':'RJ',
            'Rio Grande do Norte':'RN','Rio Grande do Sul':'RS','Rondônia':'RO',
            'Roraima':'RR','Santa Catarina':'SC','São Paulo':'SP',
            'Sergipe':'SE','Tocantins':'TO',
          };
          const uf = STATE_UF[addr.state] || '';
          if (cityName) {
            setCity(uf ? `${cityName} - ${uf}` : cityName);
          } else {
            showError('Não foi possível identificar a cidade. Tente digitar manualmente.');
          }
        } catch (e) {
          logger.error('Reverse geocoding error', e);
          showError('Erro ao obter localização.');
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        logger.error('Geolocation error', err);
        showError('Permissão de localização negada.');
        setGeoLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const formData = new FormData(e.target);
      const updates = Object.fromEntries(formData.entries());
      if (photoFile) {
        updates.photoURL = await resizeImageToBase64(photoFile);
      }
      updates.city = city;
      updates.availability = availability;
      // Quando allowWhatsApp é true, expõe o número de telefone publicamente
      updates.whatsappPhone = allowWhatsApp ? (profile?.phone || '') : '';
      // Geocodificar cidade se foi alterada
      if (city && city !== profile?.city) {
        try {
          const coords = await geocodeCity(city);
          if (coords) {
            updates.lat = coords.lat;
            updates.lng = coords.lng;
          }
        } catch (geoErr) {
          logger.warn('Geocoding falhou, salvando sem coordenadas:', geoErr);
        }
      }
      await updateProfile({ ...updates, manualRating: parseFloat(manualRating) });
      navigate('/');
    } catch (err) {
      logger.error('Erro ao salvar perfil:', err);
      showError('Erro ao salvar perfil.');
    } finally {
      setUploading(false);
    }
  };

  const ratingHistory = (profile.ratingHistory || []).slice().reverse();
  const recordedDuelIds = new Set((profile.ratingHistory || []).filter(item => item.duelId).map(item => item.duelId));
  const legacyDuelEntries = (duels || []).filter(duel => duel.status === 'finished' && !recordedDuelIds.has(duel.id) && [...(duel.creatorRoster || []), ...(duel.opponentRoster || [])].some(player => player.id === userId && !player.isGuest)).map(duel => ({ duelId: duel.id, type: 'duel', date: duel.date, rating: null }));
  const historyItems = [...ratingHistory, ...legacyDuelEntries].sort((a, b) => String(b.date).localeCompare(String(a.date)));

  return (
    <>
      <Header showBack onBack={() => navigate('/')} />
      <div className="flex-1 overflow-y-auto">
        {/* Tabs */}
        <div className="flex bg-white border-b border-slate-100 px-2">
          {[
            { id: 'perfil', label: 'Perfil' },
            { id: 'historico', label: 'Histórico', count: historyItems.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {tab.label}
              {tab.count > 0 && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{tab.count}</span>}
              {activeTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
            </button>
          ))}
        </div>

        {activeTab === 'perfil' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Editar Perfil</h2>
            <form onSubmit={handleSubmit}>
              <div className="lg:grid lg:grid-cols-2 lg:gap-8 space-y-5 lg:space-y-0">
                {/* Coluna esquerda: avatar + identidade */}
                <div className="space-y-5">
                  {/* Foto de perfil */}
                  <div className="flex items-center gap-4">
                    <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-3xl border-2 border-slate-200">{profile.photo || '👤'}</div>
                      )}
                      <div className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                        <Camera className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-700">Foto de perfil</div>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs text-blue-600 font-medium">Alterar foto</button>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                  </div>
                  <AvatarPickerRadio defaultValue={profile.photo} />
                  <input name="name" defaultValue={profile.name} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" required placeholder="Nome" />
                  <PhoneInput55Field defaultValue={profile.phone} />
                </div>
                {/* Coluna direita: atributos */}
                <div className="space-y-5 mt-5 lg:mt-0">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Cidade</label>
                    <CityAutocomplete value={city} onChange={setCity} />
                    <button
                      type="button"
                      onClick={handleUseLocation}
                      disabled={geoLoading}
                      className="mt-1.5 flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-700 disabled:text-slate-400"
                    >
                      <Navigation className="w-3 h-3" />
                      {geoLoading ? 'Obtendo localização...' : 'Usar minha localização'}
                    </button>
                  </div>
                  <select name="position" defaultValue={profile.position} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none">
                    <option value="Goleiro">Goleiro</option>
                    <option value="Defensor">Defensor</option>
                    <option value="Meia">Meia</option>
                    <option value="Atacante">Atacante</option>
                  </select>
                  <TeamPicker name="heartTeam" defaultValue={profile.heartTeam || 'Sem Time'} />
                  <select name="dominantFoot" defaultValue={profile.dominantFoot || 'Destro'} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none">
                    <option value="Destro">Destro</option>
                    <option value="Canhoto">Canhoto</option>
                    <option value="Ambidestro">Ambidestro</option>
                  </select>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2">
                    <label className="text-xs text-slate-500 font-bold">Auto-avaliação</label>
                    <StarRating rating={manualRating} setRating={setManualRating} size="w-7 h-7" />
                  </div>
                </div>
              </div>
              {/* Community rating — read-only */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex justify-between items-center mt-5">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-200 p-1.5 rounded-full"><Users className="w-4 h-4 text-blue-700" /></div>
                  <div className="text-sm font-bold text-blue-900">Nível da Galera</div>
                </div>
                <div className="flex items-center gap-1">
                  {profile.communityRating ? (
                    <span className="text-2xl font-bold text-blue-600">{profile.communityRating}</span>
                  ) : (
                    <span className="text-sm text-blue-400 italic">Sem votos</span>
                  )}
                  {profile.communityRating && <Star className="w-4 h-4 fill-blue-500 text-blue-500" />}
                </div>
              </div>
              {/* Disponibilidade */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-3">Disponibilidade</label>
                <div className="space-y-3">
                  {WEEK_DAYS.map(({ id, label }) => {
                    const intervals = availability[id];
                    const isActive = Array.isArray(intervals) && intervals.length > 0;
                    return (
                      <div key={id} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAvailability(prev => ({
                              ...prev,
                              [id]: isActive ? null : [{ start: '19:00', end: '22:00' }]
                            }))}
                            className={`w-12 text-xs font-bold py-1.5 rounded-lg transition-colors shrink-0 ${isActive ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-400'}`}
                          >{label}</button>
                          {isActive && (
                            <button
                              type="button"
                              onClick={() => setAvailability(prev => ({
                                ...prev,
                                [id]: [...prev[id], { start: '', end: '' }]
                              }))}
                              className="text-xs text-emerald-600 font-bold px-2 py-1 bg-emerald-50 rounded-lg hover:bg-emerald-100 border border-emerald-200"
                            >+ intervalo</button>
                          )}
                        </div>
                        {isActive && intervals.map((interval, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 ml-14">
                            <input
                              type="time"
                              value={interval.start}
                              onChange={e => {
                                const updated = intervals.map((iv, i) => i === idx ? { ...iv, start: e.target.value } : iv);
                                setAvailability(prev => ({ ...prev, [id]: updated }));
                              }}
                              className="w-24 p-1.5 bg-white border border-slate-200 rounded-lg text-xs outline-none"
                            />
                            <span className="text-xs text-slate-400">às</span>
                            <input
                              type="time"
                              value={interval.end}
                              onChange={e => {
                                const updated = intervals.map((iv, i) => i === idx ? { ...iv, end: e.target.value } : iv);
                                setAvailability(prev => ({ ...prev, [id]: updated }));
                              }}
                              className="w-24 p-1.5 bg-white border border-slate-200 rounded-lg text-xs outline-none"
                            />
                            {intervals.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = intervals.filter((_, i) => i !== idx);
                                  setAvailability(prev => ({ ...prev, [id]: updated.length > 0 ? updated : null }));
                                }}
                                className="text-red-400 hover:text-red-600 text-sm font-bold px-1 leading-none"
                              >×</button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* WhatsApp para convites */}
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3 mt-4">
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-emerald-500 shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <div>
                    <div className="text-sm font-medium text-slate-700">Permitir convite pelo WhatsApp</div>
                    <div className="text-xs text-slate-400">Outros jogadores poderão chamar-te para jogar</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAllowWhatsApp(v => !v)}
                  className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${allowWhatsApp ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${allowWhatsApp ? 'left-[26px]' : 'left-0.5'}`} />
                </button>
              </div>
              <Button type="submit" className="w-full mt-6" disabled={uploading}>{uploading ? 'Salvando...' : 'Salvar'}</Button>
            </form>
          </div>
        )}

        {activeTab === 'historico' && (
          <div className="p-6">
            {/* Stats Dashboard */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                <Calendar className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-blue-600">{playerStats.games}</div>
                <div className="text-[10px] text-blue-500 font-medium uppercase">Jogos</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                <Target className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-emerald-600">{playerStats.goals}</div>
                <div className="text-[10px] text-emerald-500 font-medium uppercase">Gols</div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                <Award className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-amber-600">{playerStats.assists}</div>
                <div className="text-[10px] text-amber-500 font-medium uppercase">Assist.</div>
              </div>
            </div>

            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" /> Últimas Notas
            </h2>
            {historyItems.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <div className="text-3xl mb-3">📊</div>
                <p className="text-sm">Nenhuma avaliação ainda.</p>
                <p className="text-xs mt-1">Jogue e receba votos para ver seu histórico.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {historyItems.map((h, i) => (
                  <button
                    key={i}
                    disabled={h.type === 'duel' && h.rating == null}
                    className="w-full flex items-center justify-between bg-white p-3 rounded-xl border border-slate-100 hover:bg-slate-50 active:bg-slate-100 text-left disabled:cursor-default"
                    onClick={() => h.rating != null && openVoteDetail(h)}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-700">
                        {parseLocalDate(h.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                      <div className={`text-[10px] font-medium mt-0.5 ${h.type === 'duel' ? 'text-blue-500' : 'text-emerald-500'}`}>{h.type === 'duel' ? (h.rating == null ? 'Confronto · sem votação' : 'Confronto · ver nota recebida') : 'ver notas recebidas'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {h.rating != null ? <div className="flex items-center gap-1">
                        <Star className={`w-4 h-4 ${h.rating >= 7 ? 'fill-amber-400 text-amber-400' : h.rating >= 5 ? 'fill-slate-400 text-slate-400' : 'fill-red-400 text-red-400'}`} />
                        <span className={`font-bold text-lg ${h.rating >= 7 ? 'text-amber-600' : h.rating >= 5 ? 'text-slate-600' : 'text-red-500'}`}>{h.rating}</span>
                      </div> : <span className="text-xs text-slate-400">Sem nota</span>}
                      {h.rating != null && <ChevronRight className="w-4 h-4 text-slate-300" />}
                    </div>
                  </button>
                ))}
                {profile.communityRating && (
                  <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                    <div className="text-xs font-bold text-blue-500 uppercase mb-1">Média Geral</div>
                    <div className="text-3xl font-bold text-blue-600">{profile.communityRating} <Star className="w-5 h-5 fill-blue-500 text-blue-500 inline" /></div>
                    <div className="text-xs text-blue-400 mt-1">Baseado em {ratingHistory.length} jogo{ratingHistory.length !== 1 ? 's' : ''}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>

      <VoteDetailModal
        isOpen={!!voteDetail}
        onClose={() => setVoteDetail(null)}
        player={profile}
        distribution={voteDetail?.distribution}
        outlierVotes={voteDetail?.outlierVotes}
        filteredAvg={voteDetail?.filteredAvg}
        totalVotes={voteDetail?.totalVotes}
        noData={voteDetail?.noData}
      />
    </>
  );
};

export default ProfilePage;
